import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckIcon, SparklesIcon, RocketIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
export function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const {
    t,
    getPath
  } = useLanguage();
  return <section id="services" ref={ref} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-197">
      <div className="relative z-10 mx-auto max-w-7xl" data-id="element-198">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="mb-12 sm:mb-16 text-center" data-id="element-199">
          <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
          color: 'var(--text-primary)'
        }} data-id="element-200">
            {t('pricing.title')}{' '}
            <span style={{
            color: 'var(--accent)'
          }} data-id="element-201">
              {t('pricing.titleHighlight')}
            </span>
          </h2>
          <p className="mx-auto max-w-2xl text-base sm:text-lg" style={{
          color: 'var(--text-secondary)'
        }} data-id="element-202">
            {t('pricing.subtitle')}
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid gap-6 lg:grid-cols-2 max-w-5xl mx-auto" data-id="element-203">
          {/* Free Demo Preview */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-204">
            <div className="h-full rounded-3xl border p-8 flex flex-col" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-205">
              <div className="flex items-center gap-3 mb-6" data-id="element-206">
                <div className="p-3 rounded-xl" style={{
                backgroundColor: 'var(--bg-tertiary)'
              }} data-id="element-207">
                  <SparklesIcon className="w-6 h-6" style={{
                  color: 'var(--accent)'
                }} data-id="element-208" />
                </div>
                <h3 className="text-xl font-bold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-209">
                  {t('pricing.demo.title')}
                </h3>
              </div>

              <div className="mb-6" data-id="element-210">
                <div className="inline-block px-4 py-1 rounded-full text-sm font-bold mb-2" style={{
                backgroundColor: 'var(--accent)',
                color: '#fff'
              }} data-id="element-211">
                  {t('pricing.demo.label')}
                </div>
                <p className="text-sm sm:text-base font-medium mt-2" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-212">
                  {t('pricing.demo.desc')}
                </p>
              </div>

              <ul className="space-y-4 mb-8 flex-1" data-id="element-213">
                {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-214">
                    <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-215">
                      <CheckIcon className="w-3 h-3 text-green-500" data-id="element-216" />
                    </div>
                    <span style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-217">
                      {t(`pricing.demo.b${i}`)}
                    </span>
                  </li>)}
              </ul>

              <a href={getPath('#consultation')} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02]" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-218">
                {t('pricing.demo.cta')}
              </a>
            </div>
          </motion.div>

          {/* Website Launch */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-219">
            <div className="h-full rounded-3xl border p-8 flex flex-col relative overflow-hidden" style={{
            borderColor: 'var(--border)',
            backgroundColor: 'var(--bg-secondary)'
          }} data-id="element-220">
              <div className="flex items-center gap-3 mb-6 relative z-10" data-id="element-221">
                <div className="p-3 rounded-xl" style={{
                backgroundColor: 'var(--bg-tertiary)'
              }} data-id="element-222">
                  <RocketIcon className="w-6 h-6" style={{
                  color: 'var(--accent)'
                }} data-id="element-223" />
                </div>
                <h3 className="text-xl font-bold" style={{
                color: 'var(--text-primary)'
              }} data-id="element-224">
                  {t('pricing.launch.title')}
                </h3>
              </div>

              <div className="mb-6 relative z-10" data-id="element-225">
                <div className="flex items-baseline gap-2" data-id="element-226">
                  <span className="text-4xl font-bold" style={{
                  color: 'var(--text-primary)'
                }} data-id="element-227">
                    {t('pricing.launch.price')}
                  </span>
                  <span className="text-sm" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-228">
                    {t('pricing.launch.period')}
                  </span>
                </div>
                <p className="text-sm sm:text-base font-medium mt-2" style={{
                color: 'var(--text-secondary)'
              }} data-id="element-229">
                  {t('pricing.launch.desc')}
                </p>
              </div>

              <ul className="space-y-4 mb-8 flex-1 relative z-10" data-id="element-230">
                {[1, 2, 3].map(i => <li key={i} className="flex items-start gap-3" data-id="element-231">
                    <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-232">
                      <CheckIcon className="w-3 h-3 text-green-500" data-id="element-233" />
                    </div>
                    <span style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-234">
                      {t(`pricing.launch.b${i}`)}
                    </span>
                  </li>)}
              </ul>

              <a href={getPath('#consultation')} className="block w-full rounded-xl py-4 text-center text-sm sm:text-base font-semibold transition-all hover:scale-[1.02] relative z-10" style={{
              backgroundColor: 'var(--accent)',
              color: '#FFFFFF'
            }} data-id="element-235">
                {t('pricing.launch.cta')}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}